import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SectionHeading from '../components/ui/SectionHeading';
import SafeIcon from '../common/SafeIcon';
import { careerHistory, techStack, personalInfo } from '../data/content';

const About = () => {
  return (
    <div className="pt-24 min-h-screen bg-white dark:bg-brand-dark transition-colors duration-300">
      {/* Intro with Faded Grid */}
      <section className="py-16 md:py-24 relative">
        <div 
          className="absolute inset-0 opacity-[0.08] dark:opacity-[0.12] pointer-events-none" 
          style={{ 
            backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)', 
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)',
            WebkitMaskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)'
          }}
        ></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-start">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="w-full lg:w-5/12 shrink-0"
            >
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900">
                  <img 
                    src="/facetune.jpeg"
                    alt={personalInfo.name} 
                    className="w-full aspect-[3/4] md:aspect-[4/5] object-cover object-top grayscale-[20%] hover:grayscale-0 transition-all duration-700"
                  />
                </div>
              </div>
            </motion.div>

            <div className="w-full lg:w-7/12">
              <SectionHeading title="Strategic Orchestration" subtitle="Expertise & Vision" />
              
              <div className="prose prose-lg dark:prose-invert text-slate-600 dark:text-slate-300 font-medium leading-relaxed max-w-none mt-8">
                <p className="text-2xl font-heading font-bold text-brand-dark dark:text-white mb-6">
                  I solve the $80K Tool Trap™—reclaiming strategic clarity from operational chaos.
                </p>
                <p className="mb-6">
                  Holding a <span className="font-bold text-brand-cyan">Master of Science in Digital Marketing (MSDM)</span>, I combine academic rigor with practical execution. After 16 years founding and scaling JAC Marketing—where I shaped brand strategy for clients ranging from Grammy-winning artists to Fortune 500 executives—I diagnosed a fundamental problem inside a $2B manufacturing organization: marketing teams drowning in disconnected tools and fragmented data.
                </p>
                <div className="flex flex-wrap gap-4 mb-8">
                  <a 
                    href={personalInfo.calendar} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex items-center gap-2 px-8 py-4 bg-brand-red text-white text-sm uppercase font-bold tracking-widest hover:bg-brand-yellow hover:text-brand-dark transition-all duration-300 rounded shadow-lg shadow-brand-red/20"
                  >
                    <SafeIcon name="Calendar" className="w-4 h-4" /> Book a Strategy Call
                  </a>
                </div>
                <p className="mb-6">
                  As the author of <span className="text-brand-red dark:text-brand-yellow font-bold">Brand Like A Girl</span>, I equip leaders with actionable frameworks to cultivate authentic and powerful brands. Today, through SEVEN HATS®, I help B2B SaaS and growth-stage companies build marketing functions from the ground up using LEAN and OKR frameworks.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Julie Adams is Different */}
      <section className="py-16 md:py-24 bg-white dark:bg-brand-dark border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading title="Competitive Advantage" subtitle="Why I Am Different" align="center" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="p-8 border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 rounded-2xl"
            >
              <div className="w-12 h-12 bg-brand-cyan/20 rounded-xl flex items-center justify-center mb-6">
                <SafeIcon name="Layers" className="w-6 h-6 text-brand-cyan" />
              </div>
              <h3 className="text-xl font-heading font-bold text-brand-dark dark:text-white mb-4">SEVEN HATS® Strategic Operations Platform</h3>
              <p className="text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
                My proprietary command center where marketing strategy becomes operational reality. Built on Design Thinking principles and inspired by Dr. Edward de Bono's Six Thinking Hats, SEVEN HATS® ensures every operational decision aligns with overarching brand strategy—preventing the common disconnect between high-level vision and daily execution.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="p-8 border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 rounded-2xl"
            >
              <div className="w-12 h-12 bg-brand-yellow/20 rounded-xl flex items-center justify-center mb-6">
                <SafeIcon name="Shuffle" className="w-6 h-6 text-brand-yellow" />
              </div>
              <h3 className="text-xl font-heading font-bold text-brand-dark dark:text-white mb-4">Bridging MarkOps & Marketing Strategy</h3>
              <p className="text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
                I translate marketing vision into technical architecture. By aligning these often-siloed departments, we eliminate redundant tools, unify data structures, and accelerate time-to-market.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="p-8 border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 rounded-2xl"
            >
              <div className="w-12 h-12 bg-brand-red/20 rounded-xl flex items-center justify-center mb-6">
                <SafeIcon name="Cpu" className="w-6 h-6 text-brand-red" />
              </div>
              <h3 className="text-xl font-heading font-bold text-brand-dark dark:text-white mb-4">AI Orchestration Focus</h3>
              <p className="text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
                Beyond simply adopting AI tools, I orchestrate AI into your core workflows. This creates intelligent automation loops that multiply team output and provide actionable strategic foresight.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Career Timeline */}
      <section className="py-16 md:py-24 bg-slate-50 dark:bg-brand-darker border-y border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <SectionHeading title="Professional Trajectory" subtitle="Experience" />
          
          <div className="mt-16 space-y-12">
            {careerHistory.map((role, index) => (
              <motion.div 
                key={index}
                id={role.company.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative pl-8 md:pl-0"
              >
                <div className="md:grid md:grid-cols-4 md:gap-8 items-start">
                  <div className="md:col-span-1 mb-4 md:mb-0 md:text-right md:pr-8 pt-1">
                    <span className="text-sm font-bold text-brand-dark dark:text-white uppercase tracking-widest">{role.period}</span>
                  </div>
                  <div className="md:col-span-3 pb-8 md:pb-12 relative">
                    <motion.div
                      className="absolute left-[-2rem] top-0 bottom-0 border-l-2 border-dashed border-brand-cyan/50 dark:border-brand-cyan/50 origin-top"
                      initial={{ scaleY: 0 }}
                      whileInView={{ scaleY: 1 }}
                      viewport={{ once: false, amount: 0.1 }}
                      transition={{ duration: 0.8, ease: "easeOut", delay: 0.5 }}
                    ></motion.div>
                    <motion.div
                      className="absolute left-[-2.35rem] top-2 w-3 h-3 bg-brand-red rounded-full z-10"
                      initial={{ scale: 0, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: false, amount: 0.1 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                    ></motion.div>
                    <h3 className="text-2xl font-heading font-bold text-brand-dark dark:text-white mb-2">{role.role}</h3>
                    <h4 className="text-lg font-bold text-brand-cyan mb-4">{role.company}</h4>
                    <p className="text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                      {role.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-16 md:py-24 bg-brand-dark dark:bg-brand-darker text-white relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-[0.08] dark:opacity-[0.12] pointer-events-none" 
          style={{ 
            backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)', 
            backgroundSize: '40px 40px',
            maskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)',
            WebkitMaskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)'
          }}
        ></div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          <SectionHeading title="Strategic Stack" subtitle="Technology & Methodology" className="text-white" />
          
          <div className="mt-12 flex flex-wrap gap-4">
            {techStack.map((tech, index) => (
              <motion.span 
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="px-6 py-3 bg-slate-800/50 border border-brand-cyan/30 text-white text-sm font-bold rounded-full hover:bg-brand-cyan hover:text-brand-dark transition-colors cursor-default"
              >
                {tech}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* LinkedIn Recommendations Widget */}
      <section className="py-16 md:py-24 bg-white dark:bg-brand-dark border-y border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading title="Recommendations" subtitle="Professional Endorsements" />
          <div className="mt-12">
            <div className="elfsight-app-d6cc8448-5129-49ad-af6b-bc0907fd884a" data-elfsight-app-lazy></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;